import React, { useEffect, useMemo, useState } from 'react'
import { TreeSelect } from 'antd'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { TreeNodeNormal } from 'antd/lib/tree-select/interface'
import uuid from 'react-uuid'
import { SalesSubjectMasterType, SalesSubSubjectMasterType } from '@/models/sales-manager/sales'

type SalesType = {
  salesSubjectId: string
  salesSubjectName?: string
  salesSubSubjectId: string
  salesSubSubjectName?: string
}

type Props<T extends SalesSubjectMasterType> = {
  onClick?(): void
  onChange(value: { subjectMaster: SalesSubjectMasterType; subSubject: SalesSubSubjectMasterType | undefined }): void
  defaultValue?: SalesType
  salesSubjectMasters: T[]
  isEditMode: boolean
}
export const SubjectSelect = <T extends SalesSubjectMasterType>(props: Props<T>) => {
  const { t } = useTranslation()
  const [selectedValue, setSelectedValue] = useState<string>()
  const [defaultExpandedKeys, setDefaultExpandedKeys] = useState<string[]>([])

  const onChange = (v: string) => {
    setSelectedValue(v)
    props.salesSubjectMasters.find(master => {
      if (master.id === v) {
        props.onChange({ subjectMaster: master, subSubject: undefined })
        return true
      }
      return master.subSubjects.find(sub => {
        if (sub.id === v) {
          props.onChange({ subjectMaster: master, subSubject: sub })
          return true
        }
        return false
      })
    })
  }

  const findParentMaster = (sales: SalesType): T | undefined => {
    if (isSelectedSubSubject(sales)) {
      return props.salesSubjectMasters.find(master => master.subSubjects.find(sub => sub.id === sales.salesSubSubjectId))
    } else {
      return props.salesSubjectMasters.find(master => master.id === sales.salesSubjectId)
    }
  }

  const isSelectedSubSubject = (sales: SalesType): boolean => {
    return !!sales.salesSubSubjectName && sales.salesSubSubjectName !== '-'
  }

  const treeData = useMemo((): TreeNodeNormal[] => {
    const treeDataOfMaster = props.salesSubjectMasters.map(subjectGroup => {
      // 補助科目のみのグループは親（科目）を選択不可にするため、親の値をダミーとしておく
      const value = subjectGroup.isSubSubjectOnly ? uuid() : subjectGroup.id
      const parentNode: TreeNodeNormal = {
        title: subjectGroup.subjectName,
        label: subjectGroup.subjectName,
        value,
        key: value,
        selectable: !subjectGroup.isSubSubjectOnly,
        children: [],
      }
      const childNodes: TreeNodeNormal[] = subjectGroup.subSubjects.map(sub => {
        return {
          title: sub.subSubjectName,
          label: `${subjectGroup.subjectName}/${sub.subSubjectName}`,
          value: sub.id,
          key: sub.id,
        }
      })
      parentNode.children = childNodes
      return parentNode
    })
    // 削除済み科目のときはマスターに仮追加する
    if (props.defaultValue?.salesSubjectId && props.defaultValue?.salesSubjectName) {
      const isSelectedDeletedMaster = !findParentMaster(props.defaultValue)
      if (isSelectedDeletedMaster) {
        treeDataOfMaster.push({
          title: props.defaultValue.salesSubjectName,
          label: props.defaultValue.salesSubjectName,
          // 補助科目のみ削除のとき科目IDが有効な可能性があり重複してしまうため仮IDを使用する
          value: `${props.defaultValue.salesSubjectId}-deleted`,
          key: `${props.defaultValue.salesSubjectId}-deleted`,
          selectable: false,
          children: isSelectedSubSubject(props.defaultValue)
            ? [
                {
                  title: props.defaultValue.salesSubSubjectName,
                  label: `${props.defaultValue.salesSubjectName}/${props.defaultValue.salesSubSubjectName}`,
                  value: props.defaultValue.salesSubSubjectId,
                  key: props.defaultValue.salesSubSubjectId,
                  selectable: false,
                },
              ]
            : [],
        })
      }
    }
    return treeDataOfMaster
  }, [props.salesSubjectMasters, props.defaultValue])

  useEffect(() => {
    if (!props.defaultValue?.salesSubjectId || !props.defaultValue?.salesSubjectName) {
      setSelectedValue(undefined)
      setDefaultExpandedKeys([])
      return
    }
    const parentMaster = findParentMaster(props.defaultValue)
    if (isSelectedSubSubject(props.defaultValue)) {
      setSelectedValue(props.defaultValue.salesSubSubjectId)
      if (parentMaster) {
        setDefaultExpandedKeys([parentMaster.id])
      } else {
        setDefaultExpandedKeys([`${props.defaultValue.salesSubjectId}-deleted`])
      }
    } else {
      setSelectedValue(`${props.defaultValue.salesSubjectId}${!parentMaster ? '-deleted' : ''}`)
    }
  }, [props.salesSubjectMasters, props.defaultValue])

  const wrapperRef = React.useRef<HTMLDivElement>(null)

  return (
    <div
      ref={wrapperRef}
      css={css({
        width: '100%',
        position: 'relative',
        // 選択値・プレースホルダー（「選択してください」）を通常の太さに
        '.ant-select-selector': {
          fontWeight: 'normal',
          minHeight: '32px !important',
          height: '32px !important',
          backgroundColor: '#FFFFFF !important',
          border: '1px solid #CCCCCC !important',
          borderRadius: '16px !important',
        },
        '.ant-select-selection-item': {
          fontWeight: 'normal',
        },
        '.ant-select-selection-placeholder': {
          fontWeight: 'normal',
        },
        '.ant-select-arrow': {
          right: 8,
          marginTop: 'calc(12px / -2) !important',
          '> img': { width: 10 },
        },
      })}
    >
      <style>{`
          .sales-subject-select-popup {
            max-height: 400px !important;
          }
          .sales-subject-select-popup .ant-select-tree-node-content-wrapper  {
            max-width: 400px;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
            vertical-align: middle;
          }
          .sales-subject-select-popup .ant-select-tree-node-content-wrapper.ant-select-tree-node-selected {
            color: #f2a40b !important;
            background-color: #fafafa !important;
          }
      `}</style>

      <TreeSelect
        suffixIcon={props.isEditMode ? <img src={require('@/static/images/arrow_down_yellow.svg')} /> : <></>}
        dropdownClassName="sales-subject-select-popup"
        placeholder={t('Please select')}
        showSearch
        filterTreeNode={(inputValue: string, treeNode: any): boolean => treeNode.props.label.includes(inputValue)}
        treeNodeFilterProp="title"
        treeNodeLabelProp="label"
        treeData={treeData}
        treeDefaultExpandedKeys={defaultExpandedKeys}
        value={selectedValue}
        onChange={onChange}
        onDropdownVisibleChange={props.onClick}
        getPopupContainer={() => wrapperRef.current || document.body}
      />
    </div>
  )
}
